import { User, Smile, Sparkles, Wifi } from "lucide-react";
import { IMAGES } from "@/lib/images";
import { PageHero, SectionHeading, CTABand } from "@/components/PageBlocks";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const LEADERS = [
  { name: "Jamiel Dawson", role: "Co-Founder & Travel Experience Specialist", img: "/founders/jamiel-dawson", alt: "Jamiel Dawson, Inforno Travel founder" },
  { name: "Michelle Dawson", role: "Co-Founder & Travel Experience Specialist", img: "/founders/michelle-dawson", alt: "Michelle Dawson, Inforno Travel founder" },
];

const PRINCIPLES = [
  { icon: User, title: "Personal", desc: "Travel should reflect the people taking it." },
  { icon: Smile, title: "Easy", desc: "Planning should reduce stress, not create more of it." },
  { icon: Sparkles, title: "Thoughtful", desc: "Details matter because they shape the overall experience." },
  { icon: Wifi, title: "Connected", desc: "Technology should make planning easier while real people stay available when you need them." },
];

export default function About() {
  useSeo({ title: "About Inforno Travel", description: "Inforno Travel was founded by Jamiel and Michelle Dawson to make travel feel personal, easy, thoughtful, and connected — from first conversation through departure." });
  return (
    <div className="bg-noir min-h-screen">
      <Navbar />
      <PageHero overline="About Us" title="Travel Should Feel Personal." image={IMAGES.hero}
        sub="Inforno Travel was founded by Jamiel and Michelle Dawson. We combine thoughtful planning, personal attention, and hands-on service to make travel easier from the first conversation through departure." />

      <section className="py-24 md:py-32 px-6 md:px-12 bg-noir">
        <div className="max-w-[1500px] mx-auto">
          <div className="mb-14"><SectionHeading overline="The People Behind Inforno" title="Founded by Jamiel & Michelle Dawson" sub="Together, our founders bring more than 20 years of travel experience, combining personalized service, thoughtful planning, and a genuine passion for creating memorable journeys." /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {LEADERS.map((l, i) => (
              <Reveal key={l.name} delay={i * 0.1}>
                <div data-testid={`leader-${i}`} className="bg-noir h-full">
                  <div className="aspect-[4/5] overflow-hidden bg-[#0a0a0a]">
                    <picture>
                      <source srcSet={`${l.img}.webp`} type="image/webp" />
                      <img src={`${l.img}.jpg`} alt={l.alt} width="600" height="747" loading="lazy"
                        className="w-full h-full object-cover object-top" />
                    </picture>
                  </div>
                  <div className="p-8 md:p-10">
                    <h3 className="font-serif text-3xl font-light">{l.name}</h3>
                    <p className="font-body text-[11px] uppercase tracking-[0.2em] text-gold mt-3">{l.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-[1500px] mx-auto">
          <div className="mb-14"><SectionHeading overline="What We Believe" title="Four simple principles" /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {PRINCIPLES.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 0.08}>
                  <div className="bg-noir p-8 md:p-10 h-full">
                    <Icon size={24} className="text-gold mb-8" strokeWidth={1.2} />
                    <h3 className="font-serif text-2xl font-light mb-3">{p.title}</h3>
                    <p className="font-body text-sm text-white/55 leading-relaxed font-light">{p.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand eyebrow="Let's talk" title="Ready to plan something together?" />
      <Footer />
    </div>
  );
}
